// src/eventsettings/eventpdfpreview.jsx
// プレビューの表示部分（管理者・主催者で共通）：表用 → 自動生成の表 → 固定PDF → 自動生成の表 → 裏用 を並べて、
// 冊子のように見開きで表示する。プレビュー画面（eventpreview.jsx）が使う。
// 1ページ目（表紙）は右側に1枚だけ、以降は「2-3」「4-5」のように左右2ページずつ表示する。
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.js?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const API_URL = import.meta.env.VITE_API_URL;

const PAGE_RATIO = 516.96 / 728.28; // B5の幅÷高さ（空白ページの大きさ用）
const RENDER_WIDTH = 720; // 描画する横幅（px）。表示は、枠の幅に合わせて縮小する

// ログイン情報（Authorization）が必要なので axios で取得して、PDFの全ページを返す
const loadPages = async (url) => {
  const res = await axios.get(url, { responseType: "arraybuffer" });
  const doc = await pdfjsLib.getDocument({ data: new Uint8Array(res.data) }).promise;
  const pages = [];
  for (let n = 1; n <= doc.numPages; n += 1) pages.push(await doc.getPage(n));
  return pages;
};

// 冊子のページ（{ page } か { blank: "文言" }）を、先頭から順に作る
const buildBook = async (eventId, front, back) => {
  const fixedRes = await axios.get(`${API_URL}/api/fixed-pdfs/`);
  const fixed = fixedRes.data.data;

  const base = `${API_URL}/api/events/${eventId}/pdfs`;
  const load = async (urls) => (await Promise.all(urls.map(loadPages))).flat();

  const [frontPages, autoFrontPages, fixedPages, autoBackPages, backPages] = await Promise.all([
    load(front.map((f) => `${base}/front/${f.event_pdf_id}/`)),
    // 自動生成の表。保存はせず、取得のたびにデータから作られる
    // front：審査進行並びにリング割表 / back：犬種別エントリー状況 → 出陳犬一覧
    load([`${API_URL}/api/events/${eventId}/auto-pdfs/front/`]),
    load(fixed.map((f) => `${API_URL}/api/fixed-pdfs/${f.no}/`)),
    load([`${API_URL}/api/events/${eventId}/auto-pdfs/back/`]),
    load(back.map((f) => `${base}/back/${f.event_pdf_id}/`)),
  ]);

  const toItems = (pages) => pages.map((page) => ({ page }));
  return [
    ...(frontPages.length ? toItems(frontPages) : [{ blank: "表紙（未登録）" }]),
    ...toItems(autoFrontPages),
    ...toItems(fixedPages),
    ...toItems(autoBackPages),
    ...(backPages.length ? toItems(backPages) : [{ blank: "裏表紙（未登録）" }]),
  ];
};

// 表紙は右側に1枚だけ、裏表紙は必ず左側に1枚だけ（右は空白）にする。
// その間の内側のページは、2ページずつ並べる。内側のページが奇数枚のときは、最後の1ページの右を空白にする
// （裏表紙が、手前のページと並ばないようにするため）
const buildSpreads = (book) => {
  if (book.length === 0) return [];
  const last = book.length - 1;
  const spreads = [{ left: null, right: 0 }];
  if (last === 0) return spreads; // 表紙だけ
  for (let i = 1; i < last; i += 2) {
    spreads.push({ left: i, right: i + 1 < last ? i + 1 : null });
  }
  spreads.push({ left: last, right: null });
  return spreads;
};

const spreadLabel = (spread, total) => {
  const name = (i) => (i === 0 ? "表紙" : i === total - 1 ? "裏表紙" : `${i + 1}`);
  const parts = [spread.left, spread.right].filter((i) => i !== null).map(name);
  return parts.join(" - ");
};

/* ========================================
 * 1ページ分の描画
 * ======================================== */
function PageView({ item }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!item?.page) return undefined;
    const canvas = canvasRef.current;
    const base = item.page.getViewport({ scale: 1 });
    const viewport = item.page.getViewport({ scale: RENDER_WIDTH / base.width });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const task = item.page.render({ canvasContext: canvas.getContext("2d"), viewport });
    task.promise.catch(() => {}); // 描画の取り消し（再読み込み時）は無視する
    return () => task.cancel();
  }, [item]);

  if (!item) return <div className="evSetPreviewEmpty" />;
  if (item.blank) {
    return (
      <div className="evSetPreviewBlank" style={{ aspectRatio: PAGE_RATIO }}>
        {item.blank}
      </div>
    );
  }
  return <canvas ref={canvasRef} className="evSetPreviewCanvas" />;
}

/* ========================================
 * コンポーネント
 * ======================================== */
export default function PdfBookView({ eventId, front, back }) {
  const [state, setState] = useState({ loading: true, error: null, book: [] });

  // 表用・裏用の中身が変わったら（追加・削除・並べ替え）、プレビューを作り直す
  const key = [...front, ...back].map((f) => f.event_pdf_id).join(",") + `|${front.length}`;

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    buildBook(eventId, front, back)
      .then((book) => !cancelled && setState({ loading: false, error: null, book }))
      .catch(() => !cancelled && setState({ loading: false, error: "プレビューを作れませんでした", book: [] }));
    return () => {
      cancelled = true;
    };
    // front / back の中身の変化は key で見る
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, key]);

  const { book } = state;
  const spreads = buildSpreads(book);

  if (state.loading) return <div className="evSetLoading">読み込み中...</div>;
  if (state.error) return <div className="evSetMsg isError">{state.error}</div>;

  return (
    <>
      <div className="evSetPdfMeta">全{book.length}ページ</div>
      {spreads.map((spread, i) => (
        <div key={i} className="evSetSpreadBlock">
          <div className="evSetSpreadLabel">{spreadLabel(spread, book.length)}</div>
          <div className="evSetSpread">
            <PageView item={spread.left === null ? null : book[spread.left]} />
            <PageView item={spread.right === null ? null : book[spread.right]} />
          </div>
        </div>
      ))}
    </>
  );
}
